import { Tag, Table, Typography, Button, Drawer } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import React, { useEffect } from "react";
import { NumberFormatter, currencyFormatter } from "../../../../../../utils/validator";
import FormatDate from "../../../../../../components/FormatDate/FormatDate";
import { useStateGlobalContext } from "../../../../../../contexts/GlobalContext";
import dayjs from "dayjs";

const { Text, Title } = Typography;

const sectionTitleStyle = {
  display: "block",
  color: "#9ca3af",
  textTransform: "uppercase",
  fontSize: "9px",
  fontWeight: "bold",
  letterSpacing: "0.1em",
  marginBottom: "12px",
  borderBottom: "1px solid #e5e7eb",
  paddingBottom: "4px",
};

const cardStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: "12px",
  padding: "20px",
  backgroundColor: "#f9fafb",
};

const labelStyle = {
  fontWeight: "bold",
  color: "#6b7280",
  width: "130px",
  flexShrink: 0,
  textTransform: "uppercase",
  fontSize: "8px",
};

const valueStyle = { flex: 1, color: "#374151" };

const FieldRow = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "6px", fontSize: "11px" }}>
    <div style={labelStyle}>{label}:</div>
    <div style={valueStyle}>{children || "N/A"}</div>
  </div>
);

const diagnosisColumn = [
  { key: 1, title: "#", align: "center", dataIndex: "dx_link" },
  { key: 2, title: "DX", align: "center", dataIndex: "dx" },
  { key: 3, title: "Description", dataIndex: "diagnosis_code" },
];

const PrintSuperBill = ({ open, onClose, record, clientDetails, id }) => {
  const { patientBasicInfo, getPatientData } = useStateGlobalContext();

  useEffect(() => {
    getPatientData(id);
  }, []);

  const servicesColumn = [
    { key: 1, title: "#", align: "center", render: (_, __, index) => index + 1 },
    { key: 2, title: "Date of Service", align: "center", dataIndex: "date", render: (text) => <FormatDate date={text} /> },
    { key: 3, title: "POS", align: "center", dataIndex: "pos" },
    { key: 4, title: "Code", align: "center", dataIndex: "code" },
    { key: 5, title: "Service", align: "center", dataIndex: "service" },
    {
      key: 6, title: "DX", align: "center", dataIndex: "dx",
      render: (text) => <>{text?.map((dx, i) => <Tag key={i}>{dx}</Tag>)}</>,
    },
    {
      key: 7, title: "Modifier", align: "center", dataIndex: "modifier",
      render: (text) => <>{text?.map((m, i) => <Tag key={i}>{m}</Tag>)}</>,
    },
    { key: 8, title: "Unit", align: "center", dataIndex: "unit" },
    { key: 9, title: "Fee", align: "center", dataIndex: "fee", render: (text) => <NumberFormatter value={text} options={{ style: "decimal" }} /> },
    { key: 10, title: "Paid", align: "center", dataIndex: "paid", render: (text) => <NumberFormatter value={text} options={{ style: "decimal" }} /> },
  ];

  const calculateTotals = (data) => {
    let totalFee = 0;
    let totalPaid = 0;
    data?.forEach((item) => {
      totalFee += item.unit * item.fee;
      totalPaid += parseFloat(item.paid);
    });
    return { totalFee, totalPaid };
  };

  const { totalFee, totalPaid } = calculateTotals(record?.super_data_details);
  const fmt = (val) => currencyFormatter(val);

  const officeInfoParts = (record?.office_info || "")
    .split(",")
    .map((info) => {
      const [title, ...rest] = info.split(":");
      return { title: title?.trim(), description: rest.join(":").trim() };
    })
    .filter((p) => p.title);

  const patientAddr = [
    patientBasicInfo?.current_address_line_1,
    patientBasicInfo?.current_address_line_2,
    patientBasicInfo?.current_city,
    patientBasicInfo?.current_state,
    patientBasicInfo?.current_country,
  ]
    .filter(Boolean)
    .join(", ");

  const handlePrint = () => {
    const diagnosisRowsHtml = (record?.super_diagnosis_code || [])
      .map(
        (item, idx) => `
        <tr>
          <td style="text-align:center;">${item.dx_link || idx + 1}</td>
          <td style="text-align:center;">${item.dx || "N/A"}</td>
          <td>${item.diagnosis_code || "N/A"}</td>
        </tr>`
      )
      .join("");

    const procedureRowsHtml = (record?.super_data_details || [])
      .map(
        (item, idx) => `
        <tr>
          <td style="text-align:center;">${idx + 1}</td>
          <td style="text-align:center;">${item.date ? dayjs(item.date).format("MM/DD/YYYY") : "N/A"}</td>
          <td style="text-align:center;">${item.pos || ""}</td>
          <td style="text-align:center;">${item.code || ""}</td>
          <td style="text-align:center;">${item.service || ""}</td>
          <td style="text-align:center;">${Array.isArray(item.dx) ? item.dx.join(", ") : (item.dx || "")}</td>
          <td style="text-align:center;">${Array.isArray(item.modifier) ? item.modifier.join(", ") : (item.modifier || "")}</td>
          <td style="text-align:center;">${item.unit || ""}</td>
          <td style="text-align:right;">${fmt(item.fee)}</td>
          <td style="text-align:right;">${fmt(item.paid)}</td>
        </tr>`
      )
      .join("");

    const providerRowsHtml = (record?.provider_info || [])
      .map(
        (item) => `
        <div style="margin-bottom:8px; padding-bottom:8px; border-bottom:1px solid #e5e7eb;">
          <div class="field"><span class="lbl">Provider:</span><span class="val">${item.provider_name || "N/A"}</span></div>
          <div class="field"><span class="lbl">License:</span><span class="val">${item.license || "N/A"}</span></div>
          <div class="field"><span class="lbl">Tax ID:</span><span class="val">${item.tax_id || "N/A"}</span></div>
          <div class="field"><span class="lbl">Phone:</span><span class="val">${item.phone || "N/A"}</span></div>
          ${item.additional_data_1 ? `<div class="field"><span class="lbl">Data I:</span><span class="val">${item.additional_data_1}</span></div>` : ""}
          ${item.additional_data_2 ? `<div class="field"><span class="lbl">Data II:</span><span class="val">${item.additional_data_2}</span></div>` : ""}
        </div>`
      )
      .join("");

    const officeInfoHtml = officeInfoParts
      .map(
        (p) =>
          `<div class="field"><span class="lbl">${p.title}:</span><span class="val">${p.description}</span></div>`
      )
      .join("");

    const pDOB = patientBasicInfo?.date_of_birth
      ? dayjs(patientBasicInfo.date_of_birth).format("MM/DD/YYYY")
      : "N/A";

    const printHtml = `
      <html>
        <head>
          <title>Superbill - ${record?.superbill_id || "N/A"}</title>
          <style>
            body { font-family: 'Times New Roman', serif; padding: 30px; color: black; background: white; line-height: 1.4; margin: 0; }
            .header { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 24px; align-items: stretch; }
            .box { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; flex: 1; }
            .box-title { color: #9ca3af; font-size: 9px; text-transform: uppercase; font-weight: bold; letter-spacing: 0.1em; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; display: block; }
            .section { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; margin-bottom: 20px; }
            .field { display: flex; align-items: flex-start; margin-bottom: 5px; }
            .lbl { font-weight: bold; color: #6b7280; width: 100px; flex-shrink: 0; text-transform: uppercase; font-size: 8px; }
            .val { flex: 1; font-size: 11px; color: #374151; font-family: sans-serif; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-family: sans-serif; font-size: 11px; }
            thead tr { background: #f1f5f9; }
            th { padding: 6px 10px; text-align: center; font-size: 9px; text-transform: uppercase; color: #6b7280; font-weight: bold; border-bottom: 2px solid #e2e8f0; }
            td { padding: 6px 10px; border-bottom: 1px solid #f1f5f9; color: #374151; }
            tbody tr:last-child td { border-bottom: none; }
            .totals { border: 1px solid #d1d5db; border-radius: 8px; padding: 15px; background: #f9fafb; max-width: 280px; margin-left: auto; }
            .totals-row { display: flex; justify-content: space-between; padding: 5px 0; border-bottom: 1px solid #e5e7eb; font-family: sans-serif; font-size: 12px; }
            .totals-row:last-child { border-bottom: none; font-weight: bold; }
            @media print { body { padding: 0; } @page { margin: 12mm; } }
          </style>
        </head>
        <body>
          <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:24px;border-bottom:2px solid #000;padding-bottom:10px;">
            <div>
              <h2 style="margin:0;text-transform:uppercase;font-family:serif;">Superbill</h2>
              <div style="font-size:12px;font-family:monospace;color:#666;margin-top:4px;">ID: ${record?.superbill_id || "N/A"}</div>
            </div>
          </div>

          <div class="header">
            <div class="box">
              <span class="box-title">Practice Information</span>
              ${officeInfoHtml}
            </div>
            <div class="box">
              <span class="box-title">Provider Information</span>
              ${providerRowsHtml}
            </div>
          </div>

          <div class="section">
            <span class="box-title">Patient Demographics</span>
            <div style="display:flex;gap:40px;font-family:sans-serif;font-size:11px;">
              <div style="flex:1;">
                <div class="field"><span class="lbl">Full Name:</span><span class="val">${patientBasicInfo?.first_name || "N/A"}</span></div>
                <div class="field"><span class="lbl">Gender:</span><span class="val">${patientBasicInfo?.gender || "N/A"}</span></div>
                <div class="field"><span class="lbl">DOB:</span><span class="val">${pDOB}</span></div>
              </div>
              <div style="flex:1;">
                <div class="field"><span class="lbl">Address:</span><span class="val">${patientAddr || "N/A"}</span></div>
                <div class="field"><span class="lbl">Phone:</span><span class="val">${patientBasicInfo?.patient_phone || "N/A"}</span></div>
                <div class="field"><span class="lbl">Prior Auth #:</span><span class="val">${record?.prior_auth_number || "N/A"}</span></div>
              </div>
            </div>
          </div>

          <div class="section">
            <span class="box-title">Diagnosis</span>
            <table>
              <thead><tr><th>#</th><th>DX</th><th style="text-align:left;">Description</th></tr></thead>
              <tbody>${diagnosisRowsHtml}</tbody>
            </table>
          </div>

          <div class="section">
            <span class="box-title">Procedures</span>
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Date</th><th>POS</th><th>Code</th><th>Service</th>
                  <th>DX</th><th>Modifier</th><th>Unit</th><th style="text-align:right;">Fee</th><th style="text-align:right;">Paid</th>
                </tr>
              </thead>
              <tbody>${procedureRowsHtml}</tbody>
            </table>
          </div>

          <div class="totals">
            <div class="totals-row"><span>Total Fee</span><span>${fmt(totalFee)}</span></div>
            <div class="totals-row"><span>Total Paid</span><span>${fmt(totalPaid)}</span></div>
          </div>
        </body>
      </html>
    `;

    let iframe = document.getElementById("superbill-print-iframe");
    if (!iframe) {
      iframe = document.createElement("iframe");
      iframe.id = "superbill-print-iframe";
      iframe.style.position = "absolute";
      iframe.style.top = "-9999px";
      iframe.style.left = "-9999px";
      document.body.appendChild(iframe);
    }

    const iframeDoc = iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(printHtml);
    iframeDoc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 500);
  };

  return (
    <Drawer
      title={`Superbill: ${record?.superbill_id}`}
      placement="right"
      width="1100px"
      onClose={onClose}
      open={open}
      extra={
        <Button
          type="primary"
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          style={{ backgroundColor: "#0ea5e9", borderColor: "#0ea5e9" }}
        >
          Print Now
        </Button>
      }
    >
      <div className="bg-white">
        <div className="p-8 text-black font-serif" style={{ color: "black" }}>

          {/* ORDER HEADER */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "24px", borderBottom: "1px solid #d1d5db", paddingBottom: "8px" }}>
            <div>
              <Title level={4} style={{ margin: 0, textTransform: "uppercase" }}>Superbill</Title>
              <Text type="secondary" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                ID: {record?.superbill_id}
              </Text>
            </div>
          </div>

          {/* PRACTICE + PROVIDER — two columns */}
          <div style={{ display: "flex", gap: "20px", marginBottom: "20px" }}>
            <div style={{ flex: 1, ...cardStyle }}>
              <Text style={sectionTitleStyle}>Practice Information</Text>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                {officeInfoParts.map((p, i) => (
                  <FieldRow key={i} label={p.title}>{p.description}</FieldRow>
                ))}
              </div>
            </div>

            <div style={{ flex: 1, ...cardStyle }}>
              <Text style={sectionTitleStyle}>Provider Information</Text>
              {(record?.provider_info || []).map((item, i) => (
                <div key={i} style={{ marginBottom: "10px", paddingBottom: "10px", borderBottom: i < record.provider_info.length - 1 ? "1px solid #e5e7eb" : "none" }}>
                  <FieldRow label="Provider">{item.provider_name}</FieldRow>
                  <FieldRow label="License">{item.license}</FieldRow>
                  <FieldRow label="Tax ID">{item.tax_id}</FieldRow>
                  <FieldRow label="Phone">{item.phone}</FieldRow>
                  {item.additional_data_1 && <FieldRow label="Data I">{item.additional_data_1}</FieldRow>}
                  {item.additional_data_2 && <FieldRow label="Data II">{item.additional_data_2}</FieldRow>}
                </div>
              ))}
            </div>
          </div>

          {/* PATIENT DEMOGRAPHICS */}
          <div style={{ ...cardStyle, marginBottom: "20px" }}>
            <Text style={sectionTitleStyle}>Patient Demographics</Text>
            <div style={{ display: "flex", gap: "40px", fontSize: "11px" }}>
              <div style={{ flex: 1 }}>
                <FieldRow label="Full Name">{patientBasicInfo?.first_name}</FieldRow>
                <FieldRow label="Gender">{patientBasicInfo?.gender}</FieldRow>
                <FieldRow label="DOB"><FormatDate date={patientBasicInfo?.date_of_birth} /></FieldRow>
              </div>
              <div style={{ flex: 1 }}>
                <FieldRow label="Address">{patientAddr || "N/A"}</FieldRow>
                <FieldRow label="Phone">{patientBasicInfo?.patient_phone}</FieldRow>
                <FieldRow label="Prior Auth #">{record?.prior_auth_number}</FieldRow>
              </div>
            </div>
          </div>

          {/* DIAGNOSIS TABLE */}
          <div style={{ ...cardStyle, marginBottom: "20px" }}>
            <Text style={sectionTitleStyle}>Diagnosis</Text>
            <Table
              size="small"
              columns={diagnosisColumn}
              dataSource={record?.super_diagnosis_code}
              pagination={false}
            />
          </div>

          {/* PROCEDURES TABLE */}
          <div style={{ ...cardStyle, marginBottom: "20px" }}>
            <Text style={sectionTitleStyle}>Procedures</Text>
            <Table
              columns={servicesColumn}
              dataSource={record?.super_data_details}
              size="small"
              pagination={false}
              scroll={{ x: true }}
            />
          </div>

          {/* TOTALS */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div style={{ ...cardStyle, minWidth: "260px" }}>
              <Text style={sectionTitleStyle}>Summary</Text>
              <div style={{ display: "flex", flexDirection: "column", fontFamily: "sans-serif", fontSize: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #e5e7eb", color: "#374151" }}>
                  <span>Total Fee</span>
                  <span><NumberFormatter value={totalFee} options={{ style: "decimal" }} /></span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", fontWeight: "bold", fontSize: "13px", color: "#111827" }}>
                  <span>Total Paid</span>
                  <span><NumberFormatter value={totalPaid || 0} options={{ style: "decimal" }} /></span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Drawer>
  );
};

export default PrintSuperBill;
